import type { LoggedHere } from "./people-on-computer.js";

export const LOGGED_HERE_KEY = "safeplate_logged_here";
const MAX_PEOPLE = 100;

const volatile = new Map<string, string>();

export function parseLoggedHere(raw: string | null): LoggedHere {
  if (!raw) return {};
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return {};
  }
  const result: Record<string, string> = {};
  for (const [userId, at] of Object.entries(parsed)) {
    if (typeof at === "string" && !Number.isNaN(Date.parse(at))) {
      result[userId] = at;
    }
  }
  return result;
}

// Keeps the most recent people when the map grows past the cap.
export function recordLoggedHere(
  current: LoggedHere,
  userId: string,
  at: Date,
): LoggedHere {
  const next = { ...current, [userId]: at.toISOString() };
  const keep = Object.entries(next)
    .sort((a, b) => Date.parse(b[1]) - Date.parse(a[1]))
    .slice(0, MAX_PEOPLE);
  return Object.fromEntries(keep);
}

function readRaw(): string | null {
  try {
    return globalThis.localStorage.getItem(LOGGED_HERE_KEY);
  } catch {
    return volatile.get(LOGGED_HERE_KEY) ?? null;
  }
}

function writeRaw(value: string): void {
  try {
    globalThis.localStorage.setItem(LOGGED_HERE_KEY, value);
  } catch {
    volatile.set(LOGGED_HERE_KEY, value);
  }
}

// When each person last saved a log in this browser, by user id and by
// staff profile id (people added to the computer by a manager are known only
// by their profile).
export function readLoggedOnThisComputer(): LoggedHere {
  return parseLoggedHere(readRaw());
}

// Call after a log is saved successfully, with the person's user id and
// staff profile ids.
export function noteLoggedOnThisComputer(
  ids: string | readonly string[],
  at: Date = new Date(),
): void {
  const keys = typeof ids === "string" ? [ids] : ids;
  writeRaw(
    JSON.stringify(
      keys.reduce(
        (current, key) => recordLoggedHere(current, key, at),
        readLoggedOnThisComputer(),
      ),
    ),
  );
}
